import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { imageUrl } from '../lib/images';

const PRACTICE_AREAS = [
  {
    label: 'Miljøvurdering',
    href: '/fagomraader/miljoevurdering/',
    icon: 'fad fa-leaf',
  },
  {
    label: 'Natur, Vand & Miljø',
    href: '/fagomraader/natur-vand-miljoe/',
    icon: 'fad fa-trees',
  },
  {
    label: 'Planforhold',
    href: '/fagomraader/planforhold/',
    icon: 'fad fa-layer-group',
  },
  {
    label: 'Klima & Bæredygtighed',
    href: '/fagomraader/klima-baeredygtighed/',
    icon: 'fad fa-recycle',
  },
  {
    label: 'Projektudvikling & Bygherrerådgivning',
    href: '/fagomraader/bygherreraadgivning/',
    icon: 'fad fa-pencil',
  },
  {
    label: 'Anlægslove, ekspropriation, ledninger mv.',
    href: '/fagomraader/ekspropriation-ledninger-mv/',
    icon: 'fad fa-file-signature',
  },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const isScrolledRef = useRef(false);

  // Smooth scroll detection with HYSTERESIS to completely eliminate shaking/jumping.
  // Enter scrolled state at > 50px, exit only when scrolled back above 20px.
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY || window.pageYOffset;
          if (!isScrolledRef.current && currentY > 50) {
            isScrolledRef.current = true;
            setIsScrolled(true);
          } else if (isScrolledRef.current && currentY < 20) {
            isScrolledRef.current = false;
            setIsScrolled(false);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once on mount to capture initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open to keep mobile drawer independently scrollable
  useEffect(() => {
    if (mobileMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [mobileMenuOpen]);

  // Close desktop dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Dropdown hover helpers with intent grace delay (250ms)
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 250);
  }, []);

  const isHome = location.pathname === '/' || location.pathname === '';
  const isFagomraader = location.pathname.startsWith('/fagomraader');
  const isOmMig = location.pathname === '/om-mig' || location.pathname === '/om-mig/';
  const isKontakt = location.pathname === '/kontakt' || location.pathname === '/kontakt/';

  // Helper to check active practice area
  const isPracticeAreaActive = (href) => {
    const cleanCurrent = location.pathname.replace(/\/$/, '');
    const cleanTarget = href.replace(/\/$/, '');
    return cleanCurrent === cleanTarget;
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full bg-[#34657f] text-white border-b border-[#285268] transition-[height,background-color,box-shadow,border-color] duration-300 ease-in-out ${
        isScrolled ? 'shadow-md bg-[#2d586f]' : 'shadow-sm'
      }`}
    >
      <div
        className={`max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between transition-[height,padding] duration-300 ease-in-out ${
          isScrolled ? 'h-16 sm:h-18' : 'h-20 sm:h-24'
        }`}
      >
        {/* Brand Logo - Bigger initially, slightly smaller when scrolled, perfectly smooth */}
        <Link
          to="/"
          className="flex items-center group py-1"
          aria-label="Advokatfirmaet Kopp - Forside"
          onClick={() => setMobileMenuOpen(false)}
        >
          <img
            src={imageUrl('white-logo-transparent-background.png', 'width=480,quality=90,format=auto')}
            alt="Advokatfirmaet Kopp ApS logo"
            className={`w-auto object-contain transition-all duration-300 ease-in-out group-hover:opacity-90 ${
              isScrolled
                ? 'h-9 sm:h-10 md:h-11 max-h-11'
                : 'h-12 sm:h-14 md:h-16 max-h-16'
            }`}
          />
        </Link>

        {/* Desktop Navigation (Visible ONLY on >= 1200px per strict instruction) */}
        <nav className="hidden min-[1200px]:flex items-center space-x-10 text-[13px] font-medium tracking-widest uppercase">
          <Link
            to="/"
            className={`transition-colors py-2 relative hover:text-white ${
              isHome
                ? 'text-white font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-white'
                : 'text-slate-200'
            }`}
          >
            Forside
          </Link>

          {/* Fagområder Dropdown */}
          <div
            ref={dropdownRef}
            className="relative py-2"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center">
              <Link
                to="/fagomraader/"
                className={`transition-colors pr-1.5 hover:text-white ${
                  isFagomraader ? 'text-white font-semibold' : 'text-slate-200'
                }`}
              >
                Fagområder
              </Link>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDropdownOpen((prev) => !prev);
                }}
                className="text-slate-200 hover:text-white p-1 focus:outline-none cursor-pointer"
                aria-expanded={dropdownOpen}
                aria-label="Åbn eller luk undermenu for Fagområder"
              >
                <i
                  className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Dropdown Menu with zero gap / invisible hit bridge to prevent accidental closure */}
            {dropdownOpen && (
              <div
                className="absolute right-0 min-[1350px]:left-1/2 min-[1350px]:right-auto min-[1350px]:-translate-x-1/2 top-full pt-2 w-[640px] max-w-[calc(100vw-2rem)] z-50 animate-in fade-in-50 duration-150 before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-6"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bg-white rounded-xl shadow-2xl border border-slate-100 p-6 md:p-8">
                  {/* Header / Intro inside dropdown */}
                  <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                    <span className="text-[11px] font-bold tracking-wider uppercase text-[#22435b] font-title">
                      Fagområder &amp; Rådgivning
                    </span>
                    <Link
                      to="/fagomraader/"
                      className="text-xs font-semibold text-[#34657f] hover:text-[#22435b] flex items-center gap-1.5 transition-colors group"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span>Se alle fagområder</span>
                      <i className="fa-solid fa-arrow-right text-[10px] transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </Link>
                  </div>

                  {/* 2-column Grid on desktop */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    {PRACTICE_AREAS.map((item) => {
                      const isActive = isPracticeAreaActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setDropdownOpen(false)}
                          className={`group flex items-center gap-3.5 p-2 rounded-lg transition-all duration-150 ${
                            isActive
                              ? 'bg-[#F0F4F7] ring-1 ring-[#22435B]/20'
                              : 'hover:bg-[#F0F4F7]'
                          }`}
                        >
                          {/* Circular Badge Icon (size-12/14, #F0F4F7, #22435B icon) */}
                          <div
                            className={`size-12 shrink-0 rounded-full flex items-center justify-center transition-all duration-200 ${
                              isActive
                                ? 'bg-[#22435B] text-white shadow-sm'
                                : 'bg-[#F0F4F7] text-[#22435B] group-hover:bg-[#e4ebf0] group-hover:scale-105'
                            }`}
                          >
                            <i aria-hidden="true" className={`${item.icon} text-base`} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <span
                              className={`block text-xs font-semibold tracking-wider uppercase leading-snug font-title transition-colors ${
                                isActive ? 'text-[#22435B] font-bold' : 'text-[#22435B] group-hover:text-[#142935]'
                              }`}
                            >
                              {item.label}
                            </span>
                          </div>

                          <i
                            className={`fa-solid fa-angle-right text-xs transition-transform duration-150 ${
                              isActive
                                ? 'text-[#22435B] opacity-100'
                                : 'text-[#8ba2b2] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5'
                            }`}
                            aria-hidden="true"
                          />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Link
            to="/om-mig/"
            className={`transition-colors py-2 relative hover:text-white ${
              isOmMig
                ? 'text-white font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-white'
                : 'text-slate-200'
            }`}
          >
            Om mig
          </Link>

          <Link
            to="/kontakt/"
            className={`transition-colors py-2 relative hover:text-white ${
              isKontakt
                ? 'text-white font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-white'
                : 'text-slate-200'
            }`}
          >
            Kontakt
          </Link>
        </nav>

        {/* Burger menu button (< 1200px) */}
        <div className="flex min-[1200px]:hidden items-center">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="p-2.5 text-white hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-white/40 cursor-pointer"
            aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={mobileMenuOpen}
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer (< 1200px) */}
      {/* Independently scrollable if taller than viewport, natural touch scrolling, no horizontal scroll */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-[#2d586f] border-t border-white/10 px-6 py-6 max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain overflow-x-hidden shadow-2xl transition-all duration-200">
          <nav className="flex flex-col space-y-3 text-sm uppercase tracking-wider">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-3 px-3 transition-colors rounded-none flex items-center justify-between ${
                isHome ? 'text-white font-bold bg-[#204355] border-l-4 border-white' : 'text-slate-100 hover:text-white hover:bg-[#34657f]'
              }`}
            >
              <span>Forside</span>
              {isHome && <i className="fa-solid fa-angle-right text-xs text-white" aria-hidden="true" />}
            </Link>

            {/* Mobile Fagområder item + dropdown */}
            <div className="rounded-none">
              <div className="flex items-center justify-between">
                <Link
                  to="/fagomraader/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`py-3 px-3 flex-1 transition-colors ${
                    isFagomraader && !PRACTICE_AREAS.some((item) => isPracticeAreaActive(item.href))
                      ? 'text-white font-bold bg-[#204355] border-l-4 border-white'
                      : 'text-slate-100 hover:text-white'
                  }`}
                >
                  Fagområder
                </Link>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMobileDropdownOpen((prev) => !prev);
                  }}
                  className="p-3 text-slate-200 hover:text-white cursor-pointer"
                  aria-label="Fold fagområder ud"
                  aria-expanded={mobileDropdownOpen}
                >
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                      mobileDropdownOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>

              {mobileDropdownOpen && (
                <div className="pl-3 space-y-1 pt-1 pb-2 border-l-2 border-white/20 ml-3">
                  {PRACTICE_AREAS.map((item) => {
                    const isActive = isPracticeAreaActive(item.href);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 py-2.5 px-3 text-xs normal-case tracking-normal rounded-none transition-colors ${
                          isActive
                            ? 'text-white font-semibold bg-[#204355] border-l-4 border-white shadow-xs'
                            : 'text-slate-200 hover:text-white hover:bg-[#34657f]'
                        }`}
                      >
                        <div
                          className={`size-9 rounded-full flex items-center justify-center text-xs shrink-0 ${
                            isActive ? 'bg-[#22435B] text-white' : 'bg-[#22435B]/10 text-white'
                          }`}
                        >
                          <i aria-hidden="true" className={item.icon} />
                        </div>
                        <span className="flex-1">{item.label}</span>
                        {isActive && (
                          <i className="fa-solid fa-angle-right text-[10px] text-teal-300 ml-1" aria-hidden="true" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <Link
              to="/om-mig/"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-3 px-3 transition-colors rounded-none flex items-center justify-between ${
                isOmMig ? 'text-white font-bold bg-[#204355] border-l-4 border-white' : 'text-slate-100 hover:text-white hover:bg-[#34657f]'
              }`}
            >
              <span>Om mig</span>
              {isOmMig && <i className="fa-solid fa-angle-right text-xs text-white" aria-hidden="true" />}
            </Link>

            <Link
              to="/kontakt/"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-3 px-3 transition-colors rounded-none flex items-center justify-between ${
                isKontakt ? 'text-white font-bold bg-[#204355] border-l-4 border-white' : 'text-slate-100 hover:text-white hover:bg-[#34657f]'
              }`}
            >
              <span>Kontakt</span>
              {isKontakt && <i className="fa-solid fa-angle-right text-xs text-white" aria-hidden="true" />}
            </Link>

            {/* Contact links inside mobile menu */}
            <div className="pt-6 pb-4 border-t border-white/20 flex flex-col space-y-3 text-xs normal-case tracking-normal text-slate-200">
              <a
                href="tel:004551627235"
                className="flex items-center space-x-3 text-white hover:underline py-1"
              >
                <span className="w-7 h-7 rounded-full bg-[#204355] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                </span>
                <span className="whitespace-nowrap font-medium">(+45) 5162 7235</span>
              </a>
              <a
                href="mailto:tk@kopplaw.dk"
                className="flex items-center space-x-3 text-white hover:underline py-1"
              >
                <span className="w-7 h-7 rounded-full bg-[#204355] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                </span>
                <span className="whitespace-nowrap font-medium">tk@kopplaw.dk</span>
              </a>
              <a
                href="https://www.linkedin.com/in/titti-kopp-31611517/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-white hover:underline py-1"
              >
                <span className="w-7 h-7 rounded-full bg-[#0077b5] flex items-center justify-center shrink-0">
                  <i className="fa-brands fa-linkedin-in text-xs" aria-hidden="true" />
                </span>
                <span className="whitespace-nowrap font-medium">LinkedIn</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
